import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { MapProviderModel, MapProviderType } from '@cas/types/settings';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';

export const mapProvidersWithUrl = [MapProviderType.ArcGIS, MapProviderType.GeoServer];

export const mapProvidersWithLayerName = [MapProviderType.GeoServer];

export enum MeasurementUnit {
  Metric = 'Metric',
  USCustomary = 'USCustomary',
}

export enum UserRole {
  AssetManagement = 'AssetManagement',
  GeofenceManagement = 'GeofenceManagement',
  UserManagement = 'UserManagement',
  SafetyManagement = 'SafetyManagement',
  Administrator = 'Administrator',
}

export interface UserRoleModel {
  id: UserRole;
  name: string;
  assignable: boolean;
}

export interface LocaleModel {
  id: string;
  name: string;
}

export interface LocalizationSettingsModel {
  units: MeasurementUnit;
  dateTimeNumberFormat: string; // e.g. 'en-AU'
  timezone: string; // e.g. 'Australia/Sydney'
}

export interface UiSettingsModel {
  siteName: string | null; // Max length 100 characters (arbitrary back-end decision).
  appVersion: string;
  map: MapProviderModel;
  localization: LocalizationSettingsModel;
}

export interface LibraryItemTypeModel {
  id: number;
  n: string;
  dft: string;
  cd: AssetDeviceType[];
}

export interface PersonnelSafetySettingsModel {
  trackDuress: boolean;
  trackManDown: boolean;
  externalEmailAddresses: string[]; // Limit of 50. Must be valid email addresses
  safetyManagers: string[]; // Read only. Display names of safety managers. Updated via existing user management
}

export interface PersonnelSafetySettingsRequestModel
  extends Pick<
    PersonnelSafetySettingsModel,
    'trackDuress' | 'trackManDown' | 'externalEmailAddresses'
  > {}

export const settingsApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    getRoles: build.query<UserRoleModel[], void>({
      query: () => 'api/settings/roles',
      transformResponse: (response: CollectionModel<UserRoleModel>) => response.items,
      providesTags: [{ type: QueryTag.SettingsRole, id: TagList }],
    }),
    getUiSettings: build.query<UiSettingsModel, void>({
      query: () => 'api/settings/ui',
      providesTags: (result, error, arg) => (result ? [QueryTag.SettingsUi] : []),
    }),
    putUiSettings: build.mutation<void, UiSettingsModel>({
      query: body => ({
        url: 'api/settings/ui',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, args) => (!error ? [QueryTag.SettingsUi] : []),
    }),
    getLocaleFormats: build.query<LocaleModel[], void>({
      query: () => 'api/settings/formatlocales',
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.SettingsLocale, id: TagList }] : [],
      transformResponse: ({ items }: CollectionModel<LocaleModel>, meta, arg) => items,
    }),
    getLibraryItemTypes: build.query<LibraryItemTypeModel[], void>({
      query: () => 'api/settings/libraryitemtypes',
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.SettingsLibraryItemType, id: TagList }] : [],
      transformResponse: ({ items }: CollectionModel<LibraryItemTypeModel>, meta, arg) => items,
    }),
    getPersonnelSafetySettings: build.query<PersonnelSafetySettingsModel, void>({
      query: () => 'api/settings/personnelSafety',
      providesTags: (result, error, arg) => (result ? [QueryTag.SettingsPersonnelSafety] : []),
    }),
    putPersonnelSafetySettings: build.mutation<void, PersonnelSafetySettingsRequestModel>({
      query: body => ({
        url: 'api/settings/personnelSafety',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, args) => (!error ? [QueryTag.SettingsPersonnelSafety] : []),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRolesQuery,
  useGetUiSettingsQuery,
  usePutUiSettingsMutation,
  useGetLocaleFormatsQuery,
  useGetLibraryItemTypesQuery,
  useGetPersonnelSafetySettingsQuery,
  usePutPersonnelSafetySettingsMutation,
} = settingsApi;
